import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, Plus, Clock, CheckCircle, Eye, XCircle,
  FileText, ChevronRight, Upload,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { pageContainer, fadeInUp, tapScale, staggerContainer, listItem } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { Drawer } from 'vaul'
import { useToast } from '@/components/shared/Toast'

type DisputeStatus = 'OPEN' | 'IN_REVIEW' | 'RESOLVED' | 'CLOSED'
type DisputeCategory = 'GAME_ISSUE' | 'TRANSACTION' | 'OTHER'

interface DisputeRemark {
  text: string
  time: string
  isSystem?: boolean
  isAdmin?: boolean
}

interface Dispute {
  id: string
  ticket: string
  category: DisputeCategory
  status: DisputeStatus
  description: string
  refId: string | null
  createdAt: string
  remarks: DisputeRemark[]
}

const statusConfig: Record<DisputeStatus, { label: string; color: string; bg: string; icon: typeof Clock }> = {
  OPEN: { label: '待處理', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', icon: Clock },
  IN_REVIEW: { label: '審核中', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', icon: Eye },
  RESOLVED: { label: '已解決', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle },
  CLOSED: { label: '已關閉', color: 'text-muted-foreground', bg: 'bg-foreground/5 border-foreground/10', icon: XCircle },
}

const categoryConfig: Record<DisputeCategory, { label: string; color: string; bg: string }> = {
  GAME_ISSUE: { label: '遊戲問題', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
  TRANSACTION: { label: '交易問題', color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20' },
  OTHER: { label: '其他', color: 'text-muted-foreground', bg: 'bg-foreground/5 border-foreground/10' },
}

const categoryOptions: { value: DisputeCategory; label: string }[] = [
  { value: 'GAME_ISSUE', label: '遊戲問題' },
  { value: 'TRANSACTION', label: '交易問題' },
  { value: 'OTHER', label: '其他' },
]

const statusSteps: DisputeStatus[] = ['OPEN', 'IN_REVIEW', 'RESOLVED', 'CLOSED']

const initialDisputes: Dispute[] = [
  {
    id: '1',
    ticket: 'DSP-A7K2M9',
    category: 'GAME_ISSUE',
    status: 'IN_REVIEW',
    description: '牌局 #881231 結果有疑問，我的順子應該贏得底池但系統判定對手勝出。',
    refId: '881231',
    createdAt: '2 小時前',
    remarks: [
      { text: '申訴已提交', time: '2 小時前', isSystem: true },
      { text: '狀態變更為 審核中', time: '1 小時前', isSystem: true },
    ],
  },
  {
    id: '2',
    ticket: 'DSP-B3P5Q8',
    category: 'TRANSACTION',
    status: 'RESOLVED',
    description: '昨天的提款一直顯示處理中，已超過 24 小時未到帳。',
    refId: 'TXN-20240215-XYZ',
    createdAt: '1 天前',
    remarks: [
      { text: '申訴已提交', time: '1 天前', isSystem: true },
      { text: '狀態變更為 審核中', time: '23 小時前', isSystem: true },
      { text: '已聯繫錢包供應商確認中', time: '20 小時前', isAdmin: true },
      { text: '經查證，該筆提款因網路延遲導致處理延遲，現已成功到帳。如有其他問題請再回報。', time: '12 小時前', isAdmin: true },
      { text: '狀態變更為 已解決', time: '12 小時前', isSystem: true },
    ],
  },
  {
    id: '3',
    ticket: 'DSP-C1R6N2',
    category: 'OTHER',
    status: 'CLOSED',
    description: '遊戲畫面偶爾會閃爍，特別是在翻牌階段。',
    refId: null,
    createdAt: '3 天前',
    remarks: [
      { text: '申訴已提交', time: '3 天前', isSystem: true },
      { text: '狀態變更為 審核中', time: '3 天前', isSystem: true },
      { text: '已轉交前端團隊調查', time: '2 天前', isAdmin: true },
      { text: '感謝回報，此問題已在最新版本中修復，請更新應用程式。', time: '1 天前', isAdmin: true },
      { text: '狀態變更為 已解決', time: '1 天前', isSystem: true },
      { text: '狀態變更為 已關閉', time: '1 天前', isSystem: true },
    ],
  },
  {
    id: '4',
    ticket: 'DSP-D8S3T5',
    category: 'GAME_ISSUE',
    status: 'OPEN',
    description: '牌局 #881220 的底池計算好像有誤，我的同花應該贏得全部底池。',
    refId: '881220',
    createdAt: '5 小時前',
    remarks: [
      { text: '申訴已提交', time: '5 小時前', isSystem: true },
    ],
  },
]

export function DisputesScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  const [disputes, setDisputes] = useState<Dispute[]>(initialDisputes)
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [submitOpen, setSubmitOpen] = useState(false)

  // Form state
  const [formCategory, setFormCategory] = useState<DisputeCategory | ''>('')
  const [formRefId, setFormRefId] = useState('')
  const [formDescription, setFormDescription] = useState('')

  // Check for URL params (auto-fill from hand history)
  const handId = searchParams.get('handId')
  const hasAutoFill = !!handId

  const openSubmitForm = (autoFill: boolean) => {
    if (autoFill && handId) {
      const result = searchParams.get('result')
      const handName = searchParams.get('handName')
      const amount = searchParams.get('amount')
      setFormCategory('GAME_ISSUE')
      setFormRefId(handId)
      const resultText = result === 'win' ? '贏得' : result === 'loss' ? '輸掉' : '棄牌'
      const amountVal = amount ? `$${(Math.abs(Number(amount)) / 100).toFixed(2)}` : ''
      const handNameText = handName && handName !== 'null' ? ` (${handName})` : ''
      setFormDescription(`牌局 #${handId} - 結果: ${resultText}${handNameText} - 底池 ${amountVal}`)
    } else {
      setFormCategory('')
      setFormRefId('')
      setFormDescription('')
    }
    setSubmitOpen(true)
  }

  const handleSubmit = () => {
    if (!formCategory || !formDescription.trim()) return
    const ticket = `DSP-${Date.now().toString(36).toUpperCase().slice(-6)}`
    const newDispute: Dispute = {
      id: String(Date.now()),
      ticket,
      category: formCategory,
      status: 'OPEN',
      description: formDescription.trim(),
      refId: formRefId || null,
      createdAt: '剛剛',
      remarks: [{ text: '申訴已提交', time: '剛剛', isSystem: true }],
    }
    setDisputes(prev => [newDispute, ...prev])
    setSubmitOpen(false)
    toast({ type: 'success', title: '申訴已提交', description: `工單編號: ${ticket}` })
    // Clear URL params
    if (handId) {
      navigate('/disputes', { replace: true })
    }
  }

  const openDetail = (dispute: Dispute) => {
    setSelectedDispute(dispute)
    setDetailOpen(true)
  }

  // If arriving with handId params, auto-open submit
  const [autoOpened, setAutoOpened] = useState(false)
  if (hasAutoFill && !autoOpened && !submitOpen) {
    setAutoOpened(true)
    // Delay to let page render first
    setTimeout(() => openSubmitForm(true), 300)
  }

  return (
    <motion.div
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full bg-background relative"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] gradient-radial-gold pointer-events-none" />
      <div className="fixed inset-0 noise-overlay pointer-events-none" />

      {/* Header */}
      <motion.header variants={fadeInUp} className="flex-shrink-0 flex items-center justify-between px-4 py-4 safe-area-top sticky top-0 bg-background/80 backdrop-blur-xl border-b border-foreground/5 z-40">
        <div className="flex items-center gap-4">
          <motion.button
            whileTap={tapScale}
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full glass-card hover:border-gold/30 transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </motion.button>
          <h1 className="font-display text-xl font-semibold text-foreground">我的申訴</h1>
        </div>
        <motion.button
          whileTap={tapScale}
          onClick={() => openSubmitForm(false)}
          className="flex items-center gap-2 px-3 py-2 btn-primary rounded-xl text-xs font-bold"
        >
          <Plus className="w-3.5 h-3.5" />
          新增申訴
        </motion.button>
      </motion.header>

      {/* Dispute List */}
      <motion.main
        variants={staggerContainer}
        className="flex-1 overflow-y-auto px-4 py-4 scrollbar-hide space-y-3 pb-24 relative z-10"
      >
        {disputes.length === 0 ? (
          <motion.div variants={fadeInUp} className="text-center py-16">
            <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">尚無申訴紀錄</p>
          </motion.div>
        ) : (
          disputes.map((dispute) => {
            const status = statusConfig[dispute.status]
            const category = categoryConfig[dispute.category]
            return (
              <motion.button
                key={dispute.id}
                variants={listItem}
                onClick={() => openDetail(dispute)}
                className="w-full text-left p-4 glass-card rounded-2xl hover:border-gold/20 transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-gold">{dispute.ticket}</span>
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold border', category.bg, category.color)}>
                      {category.label}
                    </span>
                  </div>
                  <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold border', status.bg, status.color)}>
                    {status.label}
                  </span>
                </div>
                <p className="text-sm text-foreground/80 line-clamp-1 mb-2">{dispute.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{dispute.createdAt}</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                </div>
              </motion.button>
            )
          })
        )}
      </motion.main>

      {/* Detail Drawer */}
      <Drawer.Root open={detailOpen} onOpenChange={setDetailOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none max-h-[85vh] flex flex-col" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 flex flex-col flex-1 overflow-hidden">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-4" />
              {selectedDispute && (() => {
                const status = statusConfig[selectedDispute.status]
                const category = categoryConfig[selectedDispute.category]
                const StatusIcon = status.icon
                const currentStepIndex = statusSteps.indexOf(selectedDispute.status)

                return (
                  <div className="flex-1 overflow-y-auto px-6 pb-8 safe-area-bottom">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <Drawer.Title className="font-mono text-lg font-bold text-gold">{selectedDispute.ticket}</Drawer.Title>
                      <span className={cn('px-2.5 py-1 rounded-lg text-[10px] font-bold border flex items-center gap-1.5', status.bg, status.color)}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </div>

                    {/* Category + Ref */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold border', category.bg, category.color)}>
                        {category.label}
                      </span>
                      {selectedDispute.refId && (
                        <span className="text-xs font-mono text-muted-foreground">
                          參考: #{selectedDispute.refId}
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    <div className="p-4 glass-card rounded-2xl mb-6">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">問題描述</p>
                      <p className="text-sm text-foreground/90 leading-relaxed">{selectedDispute.description}</p>
                    </div>

                    {/* Status Timeline */}
                    <div className="mb-6">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">處理進度</p>
                      <div className="pl-2">
                        {statusSteps.map((step, i) => {
                          const stepConfig = statusConfig[step]
                          const isActive = i <= currentStepIndex
                          const isCurrent = i === currentStepIndex
                          const isLast = i === statusSteps.length - 1
                          return (
                            <div key={step} className="flex items-start gap-3">
                              {/* Dot + Line */}
                              <div className="flex flex-col items-center">
                                <div className={cn(
                                  'w-3 h-3 rounded-full border-2 flex-shrink-0',
                                  isCurrent ? `${stepConfig.color} border-current bg-current` :
                                  isActive ? 'border-emerald-400 bg-emerald-400' :
                                  'border-foreground/20 bg-transparent'
                                )} />
                                {!isLast && (
                                  <div className={cn(
                                    'w-0.5 h-8',
                                    isActive && i < currentStepIndex ? 'bg-emerald-400/40' : 'bg-foreground/10'
                                  )} />
                                )}
                              </div>
                              {/* Label */}
                              <div className={cn('pb-4', isCurrent ? 'text-foreground' : isActive ? 'text-muted-foreground' : 'text-muted-foreground/40')}>
                                <p className={cn('text-sm font-semibold', isCurrent && stepConfig.color)}>
                                  {stepConfig.label}
                                </p>
                                {isCurrent && (
                                  <p className="text-xs text-muted-foreground mt-0.5">{selectedDispute.createdAt}</p>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Remarks Timeline */}
                    {selectedDispute.remarks.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">處理紀錄</p>
                        <div className="space-y-0 pl-2">
                          {selectedDispute.remarks.map((remark, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <div className="flex flex-col items-center">
                                <div className={cn(
                                  'w-2 h-2 rounded-full flex-shrink-0 mt-1.5',
                                  remark.isSystem ? 'bg-blue-400/60' :
                                  remark.isAdmin ? 'bg-emerald-400/60' : 'bg-gold/60'
                                )} />
                                {i < selectedDispute.remarks.length - 1 && (
                                  <div className="w-0.5 h-full min-h-[16px] bg-foreground/10" />
                                )}
                              </div>
                              <div className="pb-3 flex-1 min-w-0">
                                {remark.isAdmin && (
                                  <p className="text-[10px] font-bold text-emerald-400 mb-0.5">管理員</p>
                                )}
                                <p className={cn(
                                  'text-sm leading-relaxed',
                                  remark.isSystem ? 'text-blue-400/80 italic' :
                                  remark.isAdmin ? 'text-foreground/90' : 'text-foreground/80'
                                )}>
                                  {remark.text}
                                </p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">{remark.time}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Submit Drawer */}
      <Drawer.Root open={submitOpen} onOpenChange={(open) => {
        setSubmitOpen(open)
        if (!open) {
          setFormCategory('')
          setFormRefId('')
          setFormDescription('')
        }
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none max-h-[85vh] flex flex-col" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 flex flex-col flex-1 overflow-hidden">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-4" />
              <div className="px-6 pb-2">
                <Drawer.Title className="font-display text-xl font-semibold text-foreground">提交申訴</Drawer.Title>
              </div>
              <div className="flex-1 overflow-y-auto px-6 pb-8 safe-area-bottom">
                <div className="mt-4 space-y-5">
                  {/* Category */}
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 block">問題類型</label>
                    <div className="grid grid-cols-3 gap-2">
                      {categoryOptions.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setFormCategory(opt.value)}
                          className={cn(
                            'p-3 rounded-xl text-sm font-semibold transition-all text-center',
                            formCategory === opt.value
                              ? 'bg-gold/10 border border-gold/30 text-gold'
                              : 'glass-card text-foreground hover:border-gold/20'
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reference ID */}
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 block">
                      手牌 / 交易 ID <span className="text-muted-foreground/50 font-normal normal-case">(選填)</span>
                    </label>
                    <input
                      type="text"
                      value={formRefId}
                      onChange={(e) => setFormRefId(e.target.value)}
                      placeholder="例：881234 或 TXN-20240101-ABC"
                      className="w-full p-3 bg-surface-sunken border border-foreground/10 rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-gold/50 focus:ring-1 focus:ring-gold/30 outline-none transition-all font-mono"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">問題描述</label>
                      <span className={cn(
                        'text-xs font-mono',
                        formDescription.length > 450 ? 'text-rose-400' : 'text-muted-foreground'
                      )}>
                        {formDescription.length}/500
                      </span>
                    </div>
                    <textarea
                      value={formDescription}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormDescription(e.target.value)
                        }
                      }}
                      placeholder="請詳細描述您遇到的問題..."
                      rows={4}
                      className="w-full p-3 bg-surface-sunken border border-foreground/10 rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-gold/50 focus:ring-1 focus:ring-gold/30 outline-none transition-all resize-none"
                    />
                  </div>

                  {/* Screenshot */}
                  <div>
                    <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 block">截圖附件</label>
                    <button className="w-full p-4 border-2 border-dashed border-foreground/10 rounded-xl flex flex-col items-center gap-2 hover:border-gold/30 hover:bg-gold/5 transition-all group">
                      <Upload className="w-6 h-6 text-muted-foreground group-hover:text-gold transition-colors" />
                      <span className="text-xs text-muted-foreground group-hover:text-gold transition-colors">點擊上傳截圖</span>
                    </button>
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleSubmit}
                    disabled={!formCategory || !formDescription.trim()}
                    className={cn(
                      'w-full py-3 rounded-xl font-bold transition-all',
                      !formCategory || !formDescription.trim()
                        ? 'bg-foreground/5 text-muted-foreground cursor-not-allowed'
                        : 'btn-primary'
                    )}
                  >
                    提交申訴
                  </button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
